/* eslint-disable no-param-reassign */
/* eslint no-shadow: ["error", { "allow": ["state", "getters"] }] */
import { set } from 'lodash-es';

import {
  getStripeToken,
  payoutConfirmation,
  payoutTransaction,
  paymentCreditCard as _paymentCreditCard,
  paymentIban as _paymentIban,
  createGoCardlessBankAccount,
  getGoCardlessBankAccounts,
  paymentGoCardless,
} from '@/services/api/payment/transactions';

import {
  applyDiscountToInvoice,
} from '@/services/api/payment/invoice';

import {
  findTransactionById,
  getTransactionFromPaymentId as _getTransactionFromPaymentId,
  createTransactionFromPaymentId as _createTransactionFromPaymentId,
} from '@/services/api/payment/payment';

// initial state
const state = {
  id: null,
};

const getPaymentMethodType = (paymentMethod = {}) => {
  if (
    paymentMethod.provider === 'GOCARDLESS' ||
    paymentMethod.type === 'bank_account' ||
    paymentMethod.type === 'IBAN'
  )
    return 'IBAN';

  return 'CARD';
};

// getters
const getters = {
  id: (state) => state.id,
  transaction: (state) => state,
};

// actions
const actions = {
  async getTransactionFromPaymentId({ commit }, id) {
    const { data } = await _getTransactionFromPaymentId(id);
    commit('SET_CHECKOUT', data);
    return data;
  },
  async createTransactionFromPaymentId({ commit }, id) {
    const { data } = await _createTransactionFromPaymentId(id);
    commit('SET_CHECKOUT', data);
    return data;
  },

  /**
   ** Stripe checkout
   * use Stripe api to checkout order
   * then send transation to the backend
   * @param {stripe: Object, container: DOM Array, annual: Boolean}
   */
  payment({ rootGetters, state }, [stripe, container]) {
    const companyId = rootGetters['company/get'].id;
    const transactionId = state.id;
    const wrapper = [
      stripe, container, companyId, transactionId,
    ];
    return _paymentCreditCard(wrapper);
  },
  /**
   ** Stripe checkout
   * use Stripe api to checkout order with an iban
   * then send transation to the backend
   * @param {stripe: Object, container: DOM Array}
   */
  async paymentIban({ rootGetters, state }, [stripe, container]) {
    const companyId = rootGetters['company/get'].id;
    const transactionId = state.id;
    const customerName = state.accountOwner;
    const contactEmail = rootGetters['client/get'].email;

    const wrapper = [
      stripe, container, companyId, transactionId,
    ];
    return _paymentIban(wrapper, { customerName, contactEmail });
  },
  // TODO récupérer cette action et la réimplémenter dans le widget
  async instantPayment({ rootGetters }, { transactionId, paymentMethodId, stripe, paymentMethodType }) {
    const paymentMethods = [
      ...(rootGetters['payments/paymentMethods'] || []),
      ...(rootGetters['payments/goCardlessBankAccounts'] || []),
    ];
    const paymentMethod = paymentMethods.find(({ id }) => id === paymentMethodId);
    const resolvedPaymentMethodType = paymentMethodType || getPaymentMethodType(paymentMethod);
    const {
      data: {
        clientSecretId,
        paymentStatus,
      },
    } = await payoutTransaction(transactionId, {
      paymentMethodId,
      paymentMethodType: resolvedPaymentMethodType,
    });
    if (paymentStatus === 'requires_action') {
      const {
        paymentIntent: { id: paymentIntentId },
      } = await stripe.confirmCardPayment(clientSecretId);
      return payoutConfirmation(transactionId, { paymentMethodId, paymentIntentId });
    }
    return Promise.resolve();
  },
  /**
   ** Stripe token
   * use Stripe api to get a token
   * @param {stripe: Object, container: DOM Array, annual: Boolean}
   */
  async getStripeTokenObject(unset, [stripe, container]) {
    const tokenObject = await getStripeToken(stripe, container);
    return tokenObject;
  },
  /**
   ** Apply a coupon to the current order
   * @param {String} coupon to be used
   */
  async applyPromoCode({ dispatch, rootGetters }, coupon) {
    const id = rootGetters['invoice/id'];
    try {
      const success = await applyDiscountToInvoice(id, coupon);
      dispatch('setCheckout', success.data.checkout);
      dispatch('invoice/setInvoice', success.data, { root: true });
    } catch (err) {
      dispatch('reporting/sendError', { err, currentPage: 'Checkout' }, { root: true });
      throw (err);
    }
  },
  async getTransactionFromId({ commit, rootGetters }, transactionId) {
    if (!transactionId || transactionId === 'null' || transactionId === null || transactionId === undefined) {
      console.error('Invalid transactionId passed to getTransactionFromId:', transactionId);
      return Promise.reject(new Error(`Invalid transactionId: ${transactionId}`));
    }

    try {
      const transaction = rootGetters['transactions/get']
        .find(({ id }) => id === transactionId);
      if (transaction) {
        commit('SET_CHECKOUT', transaction);
        return Promise.resolve(transaction);
      }

      const { data } = await findTransactionById(transactionId);
      commit('SET_CHECKOUT', data);
      return Promise.resolve(data);
    } catch (err) {
      console.error('Failed to fetch transaction:', err);
      return Promise.reject(err);
    }
  },
  /**
   ** Set a value in store with path
   * @param {Object} insert { path, value } in state
   */
  setValueWithPath({ commit }, object) {
    commit('setValueWithPath', object);
  },
  clear({ commit }) {
    commit('CLEAR');
  },

  async createGoCardlessBankAccount({ rootGetters }, payload) {
    const companyId = rootGetters['risk'].id;
    return await createGoCardlessBankAccount(companyId, payload);
  },

  async getGoCardlessBankAccounts({ rootGetters }) {
    const companyId = rootGetters['risk'].id;
    return await getGoCardlessBankAccounts(companyId);
  },

  async paymentGoCardless({ commit, rootGetters, dispatch }, { transactionId, iban }) {
    if (!transactionId || transactionId === 'null' || transactionId === null || transactionId === undefined) {
      console.error('Invalid transactionId in store action:', transactionId);
      throw new Error(`Invalid transactionId in store action: ${transactionId}`);
    }

    const companyId = rootGetters['risk'].id;
    const organization = rootGetters['risk'];

    const data = await paymentGoCardless(companyId, transactionId, iban, organization);
    commit('SET_CHECKOUT', data);
    await dispatch('payments/getGoCardlessBankAccounts', null, { root: true });
    return data;
  },
};

// mutations
const mutations = {
  CLEAR(state) {
    Object.assign(state, {
      id: '',
      delegation: true,
      totalToPay: 0,
    });
  },
  SET_VALUE(state, object) {
    const [key] = Object.keys(object);
    Object.assign(state, { [key]: object[key] });
  },
  SET_CHECKOUT(state, values) {
    Object.assign(state, { ...values });
  },
  setValueWithPath(state, { value, path }) {
    set(state, path, value);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
